/**
 * agent-plugin — the `/agent` console as a studio-sibling `ComposableGenomePlugin`.
 *
 * #113: the visible agent console that RENDERS the `@ubiclouds/v5-agent-runtime`
 * engine (the turn loop, the dangerous-op gate, the live-restage seam, the event
 * trace, the memory + multimodal seams). Mirrors `devPlugin` / `ideShellPlugin`:
 * one `component` editor (`console`) routed at `/agent`, composed into the ONE
 * studio genome (gate 4 — `/agent` resolves to the registered `console` editor).
 *
 * The editor is `React.lazy` so the whole agent surface + the runtime it composes
 * stay in a split chunk OUT of the app entry (gate 8 — entry < 150 KB gz). The
 * route id is derived from the path (`/agent` → `agent`); `routesTo:
 * 'component/console'` auto-expands to this plugin's `:component/console` entity.
 *
 * @module @ubiclouds/v5-studio/agent/agent-plugin
 */

import { lazy } from 'react';
import { definePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';

/** The agent-console plugin id. */
export const AGENT_PLUGIN_ID = 'v5-studio-agent';

/** The split-chunk agent console (the `/agent` surface hosting C1–C6). */
const LazyAgentConsole = lazy(() => import('./AgentConsole.js'));

/**
 * The agent console plugin — the `/agent` route → the lazy console editor.
 * Composed as a SIBLING of `devPlugin` in `studioPlugins`; it adds no new
 * `EntityKind` (the agent surfaces lower onto `component`/`route`), so gate 4
 * stays green.
 */
export const agentPlugin: ComposableGenomePlugin = definePlugin(AGENT_PLUGIN_ID)
  .name('Agent')
  .trustTier('core')
  .editor('console', LazyAgentConsole)
  .route('/agent', { routesTo: 'component/console', title: 'Agent' })
  .build();
